import Config from '../config'
import OpeProduct from '../objects/opeproduct'
import Ajax from './ajax'


var ProductApi = {};

/**
 * ASYNC function
 * Fetch product list from API.
 *
 * @param  {string}
 * @param  {int}
 */
ProductApi.fetchProductsBasedOnEditorModeAndVendor = function (editorMode, vendor) {
	if (editorMode == null) {
		throw new Error("Cannot load products based on editor mode because the given editor mode is null");
	}

	if (vendor == null) {
		throw new Error("Cannot load products based on editor mode because the given vendor is null");
	}

	return new Promise(function (resolve, reject) {
		var productsToFetch = Config.EditorModes[editorMode];
		if (productsToFetch == null) {
			throw new Error("Cannot fetch products based on editor mode, because the config contains no products for the given editor mode: " + editorMode);
		}

		//To fetch multiple products in parallell from URL and then when all is done do something about the results,
		//we need to build an array of promises which will each fetch one product each. We then later use Promise.all to
		//make sure all of the promises (products) are loaded before we proceed to do something with the product data
		var productFetchPromises = [];

		for (var i = 0; i < productsToFetch.length; i++) {
			(function (papCode) {
				//Push a new promise into the array
				productFetchPromises.push(new Promise (function (res, rej) {
					//When the promise is run, fetch product data from url and resolve the promise with the fetched data
					Ajax.get({
						url: Config.ProductApi + '/' + papCode + '?vendor=' + vendor + '&format=xml&expanded=true&t=' + (new Date()).getTime(),
						responseType: 'XML'
					}).then(function (xml) {
						res({
								product: ProductApi.createOpeProduct(xml), 
								error: null
							});
					}).catch(function (error) {
						if (error.status === 404) {
							//404 in this case probably means they have disabled the product in the catalog
							//We want to handle this gracefully and let the editor continue working with the remaining products
							res({
								product: null, 
								error: {
										message: "Unable to load products. Please try again later. If the problem persists, please contact customer service and give them the details about the error.",
										details: "Cannot load product with pap code " + papCode + " and vendor " + vendor + ". Url: " + Config.ProductApi + '/' + papCode + '?vendor=' + vendor + '&format=xml&expanded=true', 
										error: error
									}
							});
						} else {
							rej(error)
						}
						
					});
				}));
			}(productsToFetch[i]));
		}

		//Fetch all the products using an array of promises. When all of them are fetched, this method will handle the results
		Promise.all(productFetchPromises).then(function (result) {

			var productList = [];
			var errorList = [];
			for (var i = 0; i < result.length; i++) {
				const {product, error} = result[i]
				if (product instanceof OpeProduct) {
					productList.push(product);

					//If the product has a polaroid width option among normal print widths, extract that into a virtual product.
					//This is to make handling of the polaroid easier throughout the app. Will insert back into original product at
					//final output.
					var virtualPolaroidProduct = ProductApi._createVirtualPolaroidProduct(product);
					if (virtualPolaroidProduct !== null) {
						productList.push(virtualPolaroidProduct);
					}
				}
				if (error !== null) {
					errorList.push(error);
				}
			}

			if (productList.length === 0) {
				reject(errorList[0]);
			}

			resolve(productList);
		})['catch'](function (err) {
			reject(err);
		});
	});
}

/**
 * Production wants to print polaroid prints mixed with 10cm height prints, and thus
 * the product PAP_910 has regular print widths and also a polaroid 10x12 print width.
 * However, in the editor, the polaroid needs to be displayed as a 12 height product and not a 10 height product.
 * Thus, we need to flip it. Since a product is defined as a product height with various print widths, you can't just
 * flip the height and width of ONE of the print widths of the product, and therefore we have a different solution which is
 * to extract the polaroid print width from the product and create a new virtual product with just this print width, and then
 * flip the width and height of the virtual product so that it can be displayed and calculated correctly in the editor.
 *
 * When creating the final output of the editor, we can switch back so production gets its polaroids in 10cm heights just
 * like the other 10cm heights products. This is the least messy solution we could come up with.
 *
 * Changing the product width and height in the catalog API doesn't seem like something that can be easily done,
 * although that would solve the fundamental problem and avoid this flipping and creation of virtual products
 *
 * @param  {OpeProduct} product
 * @return {OpeProduct|null}
 */
ProductApi._createVirtualPolaroidProduct = function (product) {
	var split = Config.PolaroidPrintWidth.split('-'); //This config tells us which product and width should be extracted
	if (split) {
		var polaroidProductId = split[0];
		var polaroidProductWidth = split[1]*2.54;
		if (product.getId() === polaroidProductId) {
			var widthList = product.getWidthList();
			for (var width in widthList) {
				if (width == polaroidProductWidth) {
					//Copy the options by value (not by reference) over to the new virtual product
					var virtualProductOptions = JSON.parse(JSON.stringify(product.options));

					//Create the new virtual product
					var virtualProduct = new OpeProduct(product.getId() + '_polaroid', product.getBasePrice(), virtualProductOptions);
				}
			}
		}
	}

	if (virtualProduct) {
		var productWidthList = product.getWidthList();
		var virtualProductWidthList = virtualProduct.getWidthList();

		//Remove the polaroid print width from the product
		for (var width in productWidthList) {
			if (productWidthList.hasOwnProperty(width)) {
				if (width == polaroidProductWidth) {
					product.removePrintWidth(width);
				}
			}
		}

		//Remove all other print widths from the virtual product
		for (var width in virtualProductWidthList) {
			if (virtualProductWidthList.hasOwnProperty(width)) {
				if (width != polaroidProductWidth) {
					virtualProduct.removePrintWidth(width);
				}
			}
		}

		//Flip the height and width of the virtual product
		virtualProduct.options.printWidth[virtualProduct.options.height] = virtualProduct.options.printWidth[polaroidProductWidth];
		delete virtualProduct.options.printWidth[polaroidProductWidth];
		virtualProduct.options.height = polaroidProductWidth;

		//Remove white border from virtual product
		virtualProduct.removeOptionByName('border');

		//Set custom options
		ProductApi._setPolaroidCustomSettings(virtualProduct.options);


		return virtualProduct;
	}

	return null;
}

ProductApi._setPolaroidCustomSettings = function (options) {
	var polaroidHeight = '12.200128000000001';
	var polaroidSettings = {
		canvasWidth: "8.10",
		canvasHeight: "8.10",
		invertHeightWidth: true,
		polaroid: true,
		hasLabel: true,
		// In pt
		labelFontSize: 15
	};
	// @TODO Remember to remove when this is implemented in the API

	var width = Object.keys(options.printWidth)[0];
	var height = options.height;

	options.customSettings = {};
	var key = (height + 'x' + width).toString();
	options.customSettings[key] = polaroidSettings;
}

/**
 * Based on the xml collected from the API, this will create an OpeProduct with the needed values from the xml
 * @param  {document}
 * @return {OpeProduct}
 */
ProductApi.createOpeProduct = function (xml) {
	if (!xml.childNodes) {
		throw new OpeException("Unable to load product. Please try again later. If the problem persists, please contact customer service and give them the details about the error.", {xml: xml});
	}

	var article = xml.childNodes[0];

	//Collect basic data
	var id = article.getAttribute("id");
	var visible = article.getAttribute("visible");
	if (visible === "false") {
		return null;
	}
	var basePrice = article.getElementsByTagName("financial")[0].getElementsByTagName("price")[0].childNodes[0].nodeValue;
	var options = {};

	//Collect options data
	options.label = article.getElementsByTagName("title")[0].childNodes[0].nodeValue;
	//options.printSurfaceHeight = article.getElementsByTagName("pageWidth")[0].childNodes[0].nodeValue;

	options.upsale = {};

	//Collect data about product print sizes and upsale options
	if (article.getElementsByTagName("articleExtras").length > 0) {
		var extras = article.getElementsByTagName("articleExtras")[0];

		//Print sizes
		if (extras.getElementsByTagName("printSize").length > 0) {
			var printLenghtOptionList = extras.getElementsByTagName("printSize")[0].getElementsByTagName("printSizeOption");

			options.printWidth = {};
			for (var i = 0; i < printLenghtOptionList.length; i++) {
				var printLength = printLenghtOptionList[i];
				var width = printLength.getElementsByTagName('widthInches')[0].childNodes[0].nodeValue*2.54;
				options.printWidth[width] = {};
				options.printWidth[width].title = getNodeValue( printLength,"title");
				options.printWidth[width].price = getNodeValue( printLength, "amountPrice" );
				options.printWidth[width].vendorArticleOptionId = printLength.getAttribute("vendorArticleOptionID");
				options.height = getNodeValue( printLength, 'heightInches') * 2.54;
			}
		} else {
			throw Error("Catalog API XML for product " + id + " does not contain printSize elements, which is neccessary for the prints editor to function");
		}

		//Paper options upsale
		if (extras.getElementsByTagName("paper").length > 0) {
			var paperOptionsList = extras.getElementsByTagName("paper")[0].getElementsByTagName("paperOption");
			for (var i = 0; i < paperOptionsList.length; i++) {
				var paperOption = paperOptionsList[i];
				var paperOptionId = paperOption.getAttribute("id").toLowerCase();

				options.upsale[paperOptionId] = {};
				options.upsale[paperOptionId].title = getNodeValue( paperOption, "title");
				options.upsale[paperOptionId].price = getNodeValue( paperOption, "pagePrice");
				options.upsale[paperOptionId].vendorArticleOptionId = paperOption.getAttribute("vendorArticleOptionID");
				options.upsale[paperOptionId].visible = true;
				options.upsale[paperOptionId].type = "paperType";
			}

		}

		//White border upsale
		if (extras.getElementsByTagName("border").length > 0) {
			var border = extras.getElementsByTagName("border")[0].getElementsByTagName("borderOption")[0];
			options.upsale.border = {};
			options.upsale.border.title = getNodeValue( border, "title");
			options.upsale.border.price = getNodeValue( border, "pagePrice");
			options.upsale.border.vendorArticleOptionId = border.getAttribute("vendorArticleOptionID");
			options.upsale.border.visible = true;
			options.upsale.border.type = "border";
		}

		//Image enhancement upsale
		if (extras.getElementsByTagName("imageEnhancement").length > 0) {
			var imageEnhancement = extras.getElementsByTagName("imageEnhancement")[0].getElementsByTagName("imageEnhancementOption")[0];
			options.upsale.imageEnhancement = {};
			options.upsale.imageEnhancement.title = getNodeValue( imageEnhancement, "title");
			options.upsale.imageEnhancement.price = getNodeValue( imageEnhancement, "basePrice");
			options.upsale.imageEnhancement.vendorArticleOptionId = imageEnhancement.getAttribute("vendorArticleOptionID");
			options.upsale.imageEnhancement.visible = true;
			options.upsale.imageEnhancement.type = "imageEnhancement";
			options.upsale.imageEnhancement.tooltip = _("Image enhancement will automatically optimise your images, bringing out the colour in every photo. We recommend disabling this option if you’ve already enhanced your photos with other software or apps, such as Instagram.");
		}
	}

	//If the product is the PAP_912 polaroid product, then switch height and width on the product
	//Explanation for why we do this is in the docblock of ProductApi._createVirtualPolaroidProduct
	//where we also switch height and width on a virtual product instead of a real product
	if(id === 'PAP_912') {
		var prodHeight = options.height;
		var widths = options.printWidth;
		var prodWidth = Object.keys(widths)[0];

		options.height = prodWidth;
		Object.defineProperty(options.printWidth, prodHeight,
        	Object.getOwnPropertyDescriptor(options.printWidth, prodWidth)
		);

		delete options.printWidth[prodWidth];

		ProductApi._setPolaroidCustomSettings(options);
	}

	return new OpeProduct(id, basePrice, options);
};

function getNodeValue( xmlNode, tagName ) {
	if ( xmlNode ) {
		var tag = xmlNode.getElementsByTagName(tagName)[0];

		if (tag && tag.childNodes.length > 0) {
			return tag.childNodes[0].nodeValue;
		}
	}
	return 0;
}

export default ProductApi;
